/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
import Checkbox from '@mui/material/Checkbox';
import classNames from 'classnames';
import type { ReactNodeArray, ChangeEvent } from 'react';
import { Component } from 'react';
// eslint-disable-next-line no-restricted-imports
import _ from 'underscore';
import Icon, { icons } from './Icon';

type Column = {
  title: string;
  sort?: string;
  icon?: string;
  validate?: any;
  loading?: boolean;
  width?: string;
};

type Row = {
  selected?: boolean;
};

type TableProps<T extends Row> = {
  rows: T[];
  columns: Array<Column | string>;
  sortMode?: string;
  // renderRow can optionally override how a row is rendered.
  renderRow?(data: T, index: number): ReactNodeArray;
  onRowClick?(data: T, index: number): void;
  onRowSelect?(index: number): void;
  onAllSelect?(event: ChangeEvent<HTMLInputElement>): void;
  onSort?(columnTitle: string): void;
  loading?: boolean;
  className?: string;
};

/**
 * Generic table component
 */
class Table<T extends Row> extends Component<TableProps<T>> {
  get columns() {
    return (this.props.columns || []).map(this.normalizeColumn);
  }

  get rows() {
    return this.props.rows || [];
  }

  normalizeColumn = (col: Column | string, i: number): Column => _.extend(
    {
      title: `Column ${i}`,
      sort: '',
      icon: '',
      validate: null,
      loading: true,
    },
    _.isString(col) ? { title: col, sort: col } : col,
  );

  stopPropagation = (event) => {
    event.stopPropagation();
  };

  renderRows = () => this.rows.map((row, i) => (
    <tr
      // eslint-disable-next-line no-underscore-dangle
      key={row._id || i}
      onClick={_.partial(this.props.onRowClick || _.noop, row, i)}
      className={row.selected ? 'selected' : ''}
    >
      <td
        style={{ width: '40px' }}
        onClick={this.stopPropagation}
      >
        <Checkbox
          onChange={_.partial(this.props.onRowSelect || _.noop, i)}
          defaultChecked={!!row.selected}
          checked={!!row.selected}
        />
      </td>
      {this.props.renderRow ? this.props.renderRow(row, i) : this.columns.map(col => (
        <td key={col.title}>{row[col.title]}</td>
      ))}
    </tr>
  ));

  render() {
    const renderedColumns = this.props.columns.map((rawCol, i) => {
      const col = this.normalizeColumn(rawCol, i);
      let sortIcon;
      const sortMode = this.props.sortMode || '';
      if (sortMode && col.sort) {
        if (sortMode === col.sort) {
          sortIcon = (<Icon icon={icons.caretUp} />);
        } else if (this.props.sortMode === `-${col.sort}`) {
          sortIcon = (<Icon icon={icons.caretDown} />);
        }
      }
      return (
        <th
          onClick={this.props.onSort && col.sort ? _.partial(this.props.onSort, col.sort) : null}
          key={col.title}
          style={{ width: col.width }}
          className={classNames('clickable', {
            active: col.sort && sortMode.substring(sortMode.length - col.sort.length, sortMode.length) === col.sort,
          })}>
          {col.icon ? <i className="column-icon" /> : ''}
          {col.title}
          <span className="pull-right">{sortIcon}</span>
        </th>
      );
    });

    const rows = this.renderRows();
    const isAllSelected = this.props.rows.length > 0 && this.props.rows.every(row => row.selected);

    return (
      <table className={classNames('table', 'nomargin-v', this.props.className)}>
        <thead>
          <tr>
            {this.props.onAllSelect ? (
              <th style={{ width: '30px' }}>
                <Checkbox
                  checked={isAllSelected}
                  onChange={this.props.onAllSelect}
                />
              </th>
            ) : (

              <th />
            )}
            {renderedColumns}
          </tr>
        </thead>
        <tbody className={this.props.loading ? 'loading loading-cruncher' : ''}>
          {rows.length > 0 ? (
            rows
          ) : (
            <tr className="row-knockout">
              <td className="text-center" colSpan={renderedColumns.length + 1}>
                No Records Found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  }
}

export default Table;
